import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import LeafletMap from "@/components/LeafletMap";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function VehicleDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const pickup = params.get("pickup") || "";
  const dropoff = params.get("dropoff") || "";
  const [v, setV] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios.get(`/api/vehicles/${id}`).then(r => setV(r.data)).catch(() => {});
  }, [id]);

  if (!v) return <div className="max-w-7xl mx-auto px-4 py-16">{t("vehicleDetail.cargando")}</div>;

  const gallery = (v.imagenes_galeria && v.imagenes_galeria.length > 0) ? v.imagenes_galeria : [v.imagen_url];
  const prev = () => setIdx(i => (i - 1 + gallery.length) % gallery.length);
  const next = () => setIdx(i => (i + 1) % gallery.length);

  const goCheckout = () => nav(`/checkout?vid=${v.id}${pickup && dropoff ? `&pickup=${encodeURIComponent(pickup)}&dropoff=${encodeURIComponent(dropoff)}` : ""}`);
  
  return (
    <section className="section-light py-10" data-testid="vehicle-detail">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-10">
        <div>
          <div className="divider-red mb-4"></div>
          <h1 className="font-display text-4xl sm:text-6xl" data-testid="vehicle-title">{v.marca_modelo}</h1>
        </div>

        <div className="bg-[var(--pc-grey)] p-6 rounded-lg border-l-4 border-[var(--pc-red)]">
          <p className="text-neutral-800 leading-relaxed">{v.descripcion_coche || t("vehicleDetail.descripcionPorDefecto")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-lg overflow-hidden bg-neutral-100 aspect-[4/3]">
            <img src={v.imagen_url} alt={v.marca_modelo} className="w-full h-full object-cover" />
          </div>
          <div className="rounded-lg overflow-hidden">
            <LeafletMap
              markers={[{
                lat: v.latitud,
                lng: v.longitud,
                label: v.marca_modelo,
                price: (v.precio_dia === null || v.precio_dia === undefined || v.precio_dia === "" || !Number.isFinite(Number(v.precio_dia))) ? null : `${Number(v.precio_dia).toFixed(2)} €/día`,
              }]}
              height={420}
            />
          </div>
        </div>

        <div>
          <h2 className="font-display text-2xl mb-3">{t("vehicleDetail.disponibilidad")}</h2>
          <AvailabilityCalendar vehicleId={v.id} />
        </div>

        <div className="flex justify-center">
          <button onClick={goCheckout} className="btn-red px-8 py-4 text-lg" data-testid="confirm-reserve-btn">
            {t("vehicleDetail.confirmarReservar")} · {(v.precio_dia === null || v.precio_dia === undefined || v.precio_dia === "" || !Number.isFinite(Number(v.precio_dia))) ? "—" : `${Number(v.precio_dia).toFixed(2)} ${t("vehicleDetail.diaAbrev")}`}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-black rounded-lg overflow-hidden relative" data-testid="gallery-carousel">
            <div className="aspect-[4/3] flex items-center justify-center">
              <img src={gallery[idx]} alt={`${v.marca_modelo} ${idx+1}`} className="w-full h-full object-cover" />
            </div>
            <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full" data-testid="gallery-prev"><ChevronLeft /></button>
            <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full" data-testid="gallery-next"><ChevronRight /></button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs bg-black/70 text-white px-2 py-1 rounded">{idx+1} / {gallery.length}</div>
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {v.color && (
                  <tr className="border-b border-neutral-100 bg-[var(--pc-red)]/5" data-testid="vehicle-color-row">
                    <td className="px-4 py-3 bg-[var(--pc-red)] text-white font-semibold uppercase tracking-wider text-xs w-1/2">{t("vehicleDetail.color")}</td>
                    <td className="px-4 py-3 text-neutral-900 font-display text-lg" data-testid="vehicle-color-value">{v.color}</td>
                  </tr>
                )}
                {[
                  [t("vehicleDetail.precioPorDia"), (v.precio_dia === null || v.precio_dia === undefined || v.precio_dia === "" || !Number.isFinite(Number(v.precio_dia))) ? "—" : `${Number(v.precio_dia).toFixed(2)} €`],
                  [t("vehicleDetail.transmision"), v.transmision],
                  [t("vehicleDetail.plazas"), v.plazas],
                  [t("vehicleDetail.maletero"), v.maletero],
                  [t("vehicleDetail.etiqueta"), v.etiqueta],
                  [t("vehicleDetail.anio"), v.año],
                  [t("vehicleDetail.potencia"), `${v.potencia} CV`],
                  [t("vehicleDetail.puertas"), v.puertas],
                  [t("vehicleDetail.combustible"), v.combustible],
                  [t("vehicleDetail.velocidades"), v.velocidades],
                ].map(([k, val]) => (
                  <tr key={k} className="border-b border-neutral-100">
                    <td className="px-4 py-3 bg-neutral-50 font-semibold uppercase tracking-wider text-xs w-1/2">{k}</td>
                    <td className="px-4 py-3 text-neutral-800">{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
